// WHY: single source of truth for translating a wire `RoleRef` into the
// human-readable role label rendered in actor pills (chat author, audit
// actor, future modules surfacing «who did this»). Keeps the UI policy
// off-limits to the components themselves so a future consumer cannot
// accidentally re-implement the system-vs-custom split with a divergent
// fallback.
//
// Policy:
//   1. System roles `'admin' | 'manager' | 'logist_cn' | 'logist_ru'`
//      resolve through the existing `chat.roles.<name>` i18n key set
//      (shipped in ru / en / zh-Hans). No new namespaces, no
//      `audit.roles.*` namespace. The `logist_ru` key was added with
//      #1101 to localize the RF logistician badge that previously fell
//      through to the Russian-only seed `displayName`.
//   2. The legacy `'logist'` machine name (kept around for rolling-deploy
//      / snapshot-restore compat — see
//      `apps/backend/src/roles/roles.service.ts:48` and
//      `apps/web/src/features/requests/role.ts:detectRoleKind`) folds to
//      the `logist_cn` UI bucket.
//   3. Any other role name (custom roles created by admins via the RBAC
//      catalog) renders `role.displayName` verbatim. Per
//      `apps/web/CLAUDE.md` i18n rule «don't translate user data as
//      copy: ... custom role display names», `displayName` is user data
//      and stays out of the translation layer.

import i18next, { type TFunction } from 'i18next';
import type { RoleRef } from '@magimp/shared';

// Structural translator contract used by the helper. A test or a
// non-i18next caller can pass any `(key) => string`. Production call
// sites pass the parent `i18next.t` directly (or omit the argument and
// let the helper read from the global `i18next` instance), bypassing
// the strict typed-keys constraint that would otherwise reject
// cross-namespace keys (like `requests:chat.roles.*`) at a call site
// bound to a different `useTranslation('<ns>')`.
export type RoleLabelTranslator = (key: string) => string;

const SYSTEM_ROLE_LABEL_KEY = {
  admin: 'requests:chat.roles.admin',
  manager: 'requests:chat.roles.manager',
  logist_cn: 'requests:chat.roles.logist_cn',
  logist_ru: 'requests:chat.roles.logist_ru',
} as const;

function defaultT(key: string): string {
  // i18next.t's runtime accepts arbitrary cross-namespace keys; the
  // strict TS overload only constrains the typed-keys path.
  return (i18next.t as unknown as RoleLabelTranslator)(key);
}

export function roleLabel(role: RoleRef, t: RoleLabelTranslator | TFunction = defaultT): string {
  const tx = t as RoleLabelTranslator;
  if (
    role.name === 'admin' ||
    role.name === 'manager' ||
    role.name === 'logist_cn' ||
    role.name === 'logist_ru'
  ) {
    return tx(SYSTEM_ROLE_LABEL_KEY[role.name]);
  }
  if (role.name === 'logist') {
    return tx(SYSTEM_ROLE_LABEL_KEY.logist_cn);
  }
  return role.displayName;
}
